import { create, StateCreator } from 'zustand'
import { createJSONStorage, persist } from 'zustand/middleware'
import { DivKey, GetKey } from '../utils/formatter'

import { getFutuInterval, getFutuSymbol } from '../hooks/futures/useCommon'
import { FUTU_ChangeMarginType, FUTU_GetBalance, FUTU_GetPositionList, FUTU_GetUserOpenAlgoOrders, FUTU_GetUserOpenOrders } from '@/entities/futures/api/futuApiKey'
import { MarginMode, setMarginType } from '../types/defined'

import { debounce } from '../utils/feature'
import { unPnl } from '../utils/maths'
import { throttle } from '../utils/datetimes'
import { isEmptyObject, IsValid } from '../utils/validation'
import { storeFutuExchange } from './exchange'
import { useManagementDataStore } from '@/features/management/model/managementData'
import { useAlertifyStore } from '@/widget/alertify/store/alertify'
interface FutuUserInfoData {
  futuSymbol: string // 현재 보고 있는 선물 종목
  userBalance: string // 현재 보고 있는 벨런스 USDT

  availableBalance: string // wallet 에서 지정한 이용 가능한 금액
  usedBalance: string // 현재 사용중인 금액 ( 포지션 금액 + 포지션 마진금액 + 미체결 금액 )
  unrealizedPNL: string // 계산된 미실현 손익
  equity: string // 계산된 총 자산 

  leverage: number // 현재 보고 있는 레버리지
  marginType: 'CROSS' | 'ISOLATED' // 현재 보고 있는 마진 타입

  balances: FutuAPI_GetBalance_Res[] | null // balances 원본
  positions: Map<string, FutuAPI_GetPositionList_Res> | null // 보유 포지션
  userHavePosition: Map<string, FutuAPI_GetPositionList_Res> // 사용자 보유 포지션

  openOrders: Map<string, FutuAPI_GetusesrOpenOrders_Res> | null // 보유 미체결 주문 (일반)
  algoOpenOrders: Map<string, FutuAPI_GetusesrOpenOrders_Res> | null // 보유 미체결 주문 (알고리즘)

  interval: string // '60' ,
  // timezone: 'Etc/UTC' // 'Etc/UTC'
  timezone: 'Asia/Seoul' // 'Etc/UTC'

  // 알림 관련
  unreadCnt: number // 읽지 않은 알림 개수
  notificationVersion: number // 알림 새로고침 트리거용

  InitLoginInfo: () => void

  setPositionAPI: () => void
  setOpenOrderAPI: () => void
  setAlgoOrderAPI: () => void
  getAllOpenOrders: () => Map<string, FutuAPI_GetusesrOpenOrders_Res> // 전체 주문(일반+알고) 반환

  // 웹소켓 에서 받아서 처리하는 경우
  setWSBalances: (balance: { a: string; wb: string; cw: string }) => void
  setWSPosition: (data: AccountUpdateEvent) => void
  setWSOpenOrder: (openOrder: OrderTradeUpdateEvent) => void
  setWSAlgoOrder: (algoOrder: AlgoUpdateEvent) => void
  setWSLeverage: (symbol: string, leverage: number) => void
  setWSMarginTypeChange: (symbol: string, marginType: string) => void

  getLeverage: (symbol: string) => number

  // ticker 가져와서 처리하는 구문
  getWSTicker: (ticker: Map<string, Binance24hrTicker>) => void

  // 레버리지를 변경하거나. 가격 변동으로 인한 수정.
  setPosition: (symbol: string, position: FutuAPI_GetPositionList_Res) => void
  setOpenOrder: (openOrder: FutuAPI_GetusesrOpenOrders_Res) => void

  setFutuSymbol: (symbol: string, session?: any) => void

  setBalancesAPI: () => void
  getPosition: (symbol: string, positionSide: string) => FutuAPI_GetPositionList_Res | null
  getHavePosition: (symbol: string, positionSide: string) => FutuAPI_GetPositionList_Res | null
  getHavePositionSize: (symbol: string) => [string, string]
  getunRealizedProfit: (symbol: string, positionSide: string, currentPrice: string) => [boolean, number, boolean]

  // openOrder 총 사용량 가져오기
  getOpenOrderTotalAmount: () => number | undefined
  // 사용가능 현재 수량
  getAmountToRatioCLOSE: (symbol: string, positionSide: string, ratio: number) => number

  // openOrder TP/SL 가져오기
  getOpenOrderTPSLPrice: (symbol: string, positionSide: string) => [string, string]

  getIsolatedMargin: (symbol: string, positionSide: string) => number

  setInterval: (interval: string) => void // '60' ,

  setFutuLogout: () => void

  // 알림 관련 함수
  setUnreadCnt: (count: number) => void
  incrementNotificationVersion: () => void
}

export const storeFutuUserInfo = create<FutuUserInfoData>()(
  persist(
    (set, get) => ({

      futuSymbol: getFutuSymbol() || 'BTCUSDT', // getFutuSymbol() 데이터가 없을때 예외처리 해야됨 .
      userBalance: '', // USDT  // 전체 자산
      unrealizedPNL: '', // 미실현 손익
      equity: '',
      availableBalance: '', // 이용가능 금액
      usedBalance: '', // 현재 사용 금액 (주문금액 , 미체결 주문 금액, 포지션 마진 )
      leverage: 1, // 현재 레버리지 - 정보가 position 에 있음
      marginType: 'CROSS', // 현재 마진 타입 - 정보가 position 에 있음 정보만 받고 따로 사용해도 됨

      balances: null, // balances 원본
      positions: null, // 보유 포지션
      userHavePosition: new Map<string, FutuAPI_GetPositionList_Res>(), // 사용자 보유 포지션
      openOrders: null, // 보유 미체결 주문
      algoOpenOrders: null, // 보유 미체결 주문 (알고리즘)
      interval: getFutuInterval(),
      // timezone: 'Etc/UTC',
      timezone: 'Asia/Seoul',

      // 알림 관련
      unreadCnt: 0,
      notificationVersion: 0,

      InitLoginInfo: () => {
        get().setBalancesAPI()
        get().setPositionAPI()
        get().setOpenOrderAPI()
        get().setAlgoOrderAPI()
      },
      setWSBalances: (balance: { a: string; wb: string; cw: string }) => {
        const tmpbalance = get().balances
        const updatedBalances = tmpbalance?.map((el) => {
          if (el.asset === balance.a) {
            return { ...el, balance: balance.wb } // 새로운 값으로 업데이트
          }
          return el // 나머지는 그대로 반환
        })

        set((state) => {
          const userBalance = Number(balance.wb)
          const availableBalance = userBalance - Number(state.usedBalance) - Number(state.unrealizedPNL)
          const clampedAvailable = Math.max(0, availableBalance)
          return {
            ...state,
            userBalance: balance.wb,
            balances: updatedBalances,
            availableBalance: clampedAvailable.toString(), // 음수면 0으로 표기
          }
        })
      },

      setBalancesAPI: debounce(async () => {
        try {
          const balances = await FUTU_GetBalance()
          if ('code' in balances) {
            return
          }

          const tmp = balances.filter((el) => el.asset === 'USDT')
          const { crossUnPnl } = tmp[0] // 미체결 손익
          const m_totalBalance = Number(tmp[0].balance) // USDT 전체 자산
          const m_availableBalance = Number(tmp[0].availableBalance) // 이용 가능한 금액
          const m_usedBalance = m_totalBalance - (m_availableBalance - Number(crossUnPnl)) // 사용된 금액
          const currentAvil = m_availableBalance > 0 ? m_availableBalance : 0
          set({
            balances: tmp,
            userBalance: m_totalBalance.toString(),
            usedBalance: m_usedBalance.toString(),
            availableBalance: String(currentAvil),
          })


        } catch (e) {
          console.log(e)
        }
      }, 1000),
      setPositionAPI: async () => {
        const positions = await FUTU_GetPositionList('')
        if ('code' in positions) {
          return
        }

        const positiontoMap = positions.reduce((map, position) => {
          map.set(`${position.symbol.toUpperCase()}_${position.positionSide.toUpperCase()}`, position)
          return map
        }, new Map<string, (typeof positions)[0]>())

        // 보유중이 포지션 등록
        const tmpuserHavePosition = positions.filter((el) => el.notional !== '0')
        const userHavePositionMap = tmpuserHavePosition.reduce((map, position) => {
          map.set(`${position.symbol.toUpperCase()}_${position.positionSide.toUpperCase()}`, position)
          return map
        }, new Map<string, (typeof positions)[0]>())

        // 현재 레버리지, cross 여부 확인
        const currentPosition = positions.filter((el) => el.symbol === get().futuSymbol)

        if (currentPosition.length > 0) {
          set({
            positions: positiontoMap,
            userHavePosition: userHavePositionMap,
            leverage: Number(currentPosition[0].leverage),
            marginType: setMarginType(currentPosition[0].marginType),
          })
          return
        }

        // 이건 있을 수 없는데 ?
        set({
          positions: positiontoMap,
          userHavePosition: userHavePositionMap,
        })
      },

      setPositionAPISymbol: async (symbol: string) => {
        // if (!storeUserInfo.getState().IsLogin()) return
        const positions = await FUTU_GetPositionList(symbol)
        if ('code' in positions) {
          return
        }

        const positiontoMap = positions.reduce((map, position) => {
          map.set(`${position.symbol.toUpperCase()}_${position.positionSide.toUpperCase()}`, position)
          return map
        }, new Map<string, (typeof positions)[0]>())

        // 보유중이 포지션 등록
        const tmpuserHavePosition = positions.filter((el) => el.notional !== '0')
        const userHavePositionMap = tmpuserHavePosition.reduce((map, position) => {
          map.set(`${position.symbol.toUpperCase()}_${position.positionSide.toUpperCase()}`, position)
          return map
        }, new Map<string, (typeof positions)[0]>())

        // 현재 레버리지, cross 여부 확인
        const currentPosition = positions.filter((el) => el.symbol === get().futuSymbol)
        
        if (currentPosition.length > 0) {
          set({
            positions: positiontoMap,
            userHavePosition: userHavePositionMap,
            leverage: Number(currentPosition[0].leverage),
            marginType: setMarginType(currentPosition[0].marginType),
          })
          return
        }

        // 이건 있을 수 없는데 ?
        set({
          positions: positiontoMap,
          userHavePosition: userHavePositionMap,
        })
      },

      setOpenOrderAPI: debounce(async () => {
        const openorder = await FUTU_GetUserOpenOrders()
        if ('code' in openorder) {
          return
        }
        
        const openordertoMap = openorder.reduce((map, position) => {
          map.set(position.clientOrderId, position)
          return map
        }, new Map<string, FutuAPI_GetusesrOpenOrders_Res>())
        set({ openOrders: openordertoMap })
        get().setBalancesAPI()
      }, 500),

      setAlgoOrderAPI: debounce(async () => {
        const openalgoorder = await FUTU_GetUserOpenAlgoOrders()
        if ('code' in openalgoorder) {
            return
        }

        const mappedAlgoOrders = openalgoorder.map((algo) => {
          // 화면 표시에 필요한 필드만 매핑
          return {
            ...algo,
            // 1. 식별자
            orderId: algo.algoId,
            clientOrderId: algo.clientAlgoId,
            
            // 2. 화면 표시 데이터
            time: algo.createTime,       // 시간
            symbol: algo.symbol,         // 종목
            type: algo.orderType,        // 타입
            positionSide: algo.positionSide, // 포지션
            side: algo.side,             // 매수/매도 (Open/Close 판단용)
            price: algo.price || '0',    // 가격 (없으면 0)
            origQty: algo.quantity,      // 수량
            status: algo.algoStatus,
            stopPrice: algo.triggerPrice || '0', // Stop Price
            
          } as FutuAPI_GetusesrOpenOrders_Res
        })

        const algoOrderMap = mappedAlgoOrders.reduce((map, position) => {
          map.set(position.clientOrderId, position)
          return map
        }, new Map<string, FutuAPI_GetusesrOpenOrders_Res>())

        set({ algoOpenOrders: algoOrderMap })
        get().setBalancesAPI()
      }, 500),

      getAllOpenOrders: () => {
        const openOrders = get().openOrders || new Map()
        const algoOrders = get().algoOpenOrders || new Map()
        return new Map([...openOrders, ...algoOrders])
      },
      // 마진 타입 변경  여기는 변경만 합니다.
      setMarginTypeAPI: async (symbol: string, argmargintype: MarginMode) => {
        try {
          // if (!storeUserInfo.getState().IsLogin()) return
          const m_marginType = argmargintype === MarginMode.CROSS ? 'CROSSED' : 'ISOLATED'
          const res = await FUTU_ChangeMarginType({
            symbol,
            marginType: m_marginType as 'ISOLATED' | 'CROSSED',
          })

          if ('error_code' in res) {
            console.log('마진 타입 변경 실패', res)
          }

          if ('code' in res) {
            if (res?.code !== 200) {
              console.log('마진 타입 변경 실패', res)
            }
          }
        } catch (e) {
          console.log(e)
        }
      },
      // websocket 에서 받아서 처리하는 경우
      setWSPosition: (data: AccountUpdateEvent) => {
        /**
         *  원래는 websocket 에 있는 데이터로 처리하려고 했는데
         *  이렇게 하면 청산가가 안맞는 문제가 생김.
         *  포지션이 여러개라고 가정하면 제대로 맞출 수 있을까 하는 의문이 생김
         *  그래서 이렇게 오면 trottling 으로 처리할 예정
         */

        // 신규로 다시 가져오자 !!  -- 청산가 계산이 없거나 안맞음..
        // PC에서 안맞는 문제가 좀 있긴했음.
        // get().setPositionAPI()
        try {
          const pos = new Map<string, FutuAPI_GetPositionList_Res>(get().positions)
          const havepos = new Map<string, FutuAPI_GetPositionList_Res>(get().userHavePosition)
          data.a.P.forEach((value) => {
            const getKeys = GetKey(value.s, value.ps)
            const po = pos.get(GetKey(value.s, value.ps))
            if (po === undefined) {
              return
            }
            po.entryPrice = value.ep
            po.notional = (parseFloat(value.ep) * parseFloat(value.pa)).toString()
            po.positionAmt = value.pa
            po.isolatedWallet = value.iw
            po.unRealizedProfit = value.up
            pos.set(getKeys, po)

            const tmphave = havepos.get(GetKey(value.s, value.ps))
            if (tmphave === undefined) {
              havepos.set(getKeys, po)
            } else if (tmphave !== undefined) {
              if (value.pa === '0') {
                //-- 포지션 삭제의 경우 TPSL 체크후 취소처리
                // const cancelOrderList = get().checkAutoTPSL_Cancel(value.s, value.ps)
                // if (cancelOrderList) {
                //   get().procAutoTPSLCancel(value.s, cancelOrderList)
                // }
                // havepos.delete(getKeys)
              } else {
                tmphave.entryPrice = value.ep
                tmphave.notional = (parseFloat(value.ep) * parseFloat(value.pa)).toString()
                tmphave.positionAmt = value.pa
                tmphave.isolatedWallet = value.iw
                tmphave.unRealizedProfit = value.up
                havepos.set(getKeys, tmphave)
              }
            }
          })
          // console.log('userHavePositionMap333', havepos)
          set({ positions: pos, userHavePosition: havepos })
          debounce(() => {
            get().setPositionAPI()
            // useManagementDataStore.getState().fetchBotList()
          }, 1000)()
        } catch (e) {
          console.log(e)
        }
      },
  // 애는
  setWSOpenOrder: (openOrder: OrderTradeUpdateEvent) => {

    get().setOpenOrderAPI()

    const { o } = openOrder
    if (o.x === 'NEW') {
      if (o.o === 'LIMIT' || o.o === 'STOP' || o.o === 'STOP_MARKET' || o.o === 'TAKE_PROFIT' || o.o === 'TAKE_PROFIT_MARKET' || o.o === 'TRAILING_STOP_MARKET') {
        useAlertifyStore.getState().setAlertMessage({ type: 'success', message: 'Order Submitted' })
      }
      
    } 
    else if (o.x === 'AMENDMENT') {
      useAlertifyStore.getState().setAlertMessage({ type: 'success', message: 'Order Modified' })
    }
    else if (o.x === 'CANCELED') {
      useAlertifyStore.getState().setAlertMessage({ type: 'error', message: 'Order Cancelled' })
      debounce(() => {
        useManagementDataStore.getState().fetchBotList()
        // 알림 새로고침 트리거 증가
        get().incrementNotificationVersion()
      }, 500)()
      debounce(() => {
        useManagementDataStore.getState().fetchBotList()
        // 알림 새로고침 트리거 증가
        get().incrementNotificationVersion()
      }, 3000)()
    } else if (o.x === 'CALCULATED') {
      // useAlertifyStore.getState().setAlertMessage({ type: 'error', message: 'Your position liquidated' })

    } else if (o.x === 'EXPIRED') {
      // useAlertifyStore.getState().setAlertMessage({ type: 'error', message: 'Limit Order Canceled' })

    } else if (o.x === 'TRADE') {
      if (o.o === 'MARKET') {
        useAlertifyStore.getState().setAlertMessage({ type: 'success', message: 'Market Order Completed' })
      } else {
        if (o.X === 'FILLED') {
          useAlertifyStore.getState().setAlertMessage({ type: 'success', message: 'Limit Order Completed' })
        } else if (o.X === 'PARTIALLY_FILLED') {
        }
      }
    }
    if (o.c && (o.c.includes('_T_') || o.c.includes('_TS_') || o.c.includes('STOP') || o.c.includes('_S_') || o.c.includes('_FL_') || o.c.includes('_IS_'))) {
      // console.log('봇 관련 주문 감지, 봇리스트 호출:', o.c)
      debounce(() => {
        useManagementDataStore.getState().fetchBotList()

        // 알림 새로고침 트리거 증가
        get().incrementNotificationVersion()
      }, 500)()
      debounce(() => {
        useManagementDataStore.getState().fetchBotList()

        // 알림 새로고침 트리거 증가
        get().incrementNotificationVersion()
      }, 3000)()
    }
  },

  setWSAlgoOrder: (algoOrder: AlgoUpdateEvent) => {
    
    
    // 봇 리스트 및 알림 갱신 (기존 setWSOpenOrder 로직 참조)
    debounce(() => {
      // 알고리즘 주문 변경 시 algoOrderAPI 호출로 목록 갱신
      get().setAlgoOrderAPI()

      useManagementDataStore.getState().fetchBotList()
      get().incrementNotificationVersion()
    }, 500)()
    debounce(() => {
      useManagementDataStore.getState().fetchBotList()
      get().incrementNotificationVersion()
    }, 3000)()
  },

  setWSLeverage: (symbol: string, leverage: number) => {
        const state = get()
        const LongKey = GetKey(symbol, 'LONG')
        const ShortKey = GetKey(symbol, 'SHORT')

        const updatedUserHavePosition = state.userHavePosition ? new Map(state.userHavePosition) : null
        const updatedPositions = state.positions ? new Map(state.positions) : null
        const updatedLeverage = state.futuSymbol === symbol ? leverage : null

        // 보유중인 포지션이 있는 경우 레버리지 업데이트
        if (updatedUserHavePosition) {
          const havePositionLong = updatedUserHavePosition.get(LongKey)
          if (havePositionLong) {
            havePositionLong.leverage = String(leverage)
            updatedUserHavePosition.set(LongKey, havePositionLong)
          }

          const havePositionShort = updatedUserHavePosition.get(ShortKey)
          if (havePositionShort) {
            havePositionShort.leverage = String(leverage)
            updatedUserHavePosition.set(ShortKey, havePositionShort)
          }
        }

        // 전체 포지션 업데이트
        if (updatedPositions) {
          const positionLong = updatedPositions.get(LongKey)
          if (positionLong) {
            positionLong.leverage = String(leverage)
            updatedPositions.set(LongKey, positionLong)
          }

          const positionShort = updatedPositions.get(ShortKey)
          if (positionShort) {
            positionShort.leverage = String(leverage)
            updatedPositions.set(ShortKey, positionShort)
          }
        }

        // 상태를 한 번에 업데이트
        set({
          userHavePosition: updatedUserHavePosition || state.userHavePosition,
          positions: updatedPositions || state.positions,
          leverage: updatedLeverage || state.leverage,
        })
      },
      setWSMarginTypeChange: (symbol: string, marginType: string) => {
        const state = get()
        const symbolLongkey = `${symbol.toUpperCase()}_LONG`
        const symbolShortkey = `${symbol.toUpperCase()}_SHORT`
        const thisPositionLong = state.positions?.get(symbolLongkey)
        const thisPositionShort = state.positions?.get(symbolShortkey)
        if (thisPositionLong && thisPositionShort) {
          set((state1) => {
            const newPositions = new Map(state.positions)
            thisPositionLong.marginType = marginType
            thisPositionShort.marginType = marginType
            newPositions.set(symbolLongkey, thisPositionLong)
            newPositions.set(symbolShortkey, thisPositionShort)
            return {
              ...state1,
              positions: newPositions,
              marginType: setMarginType(marginType),
            }
          })
        }
      },
      /// 현재 레버리지가 아닌 레버리지 가져오기  - 롱숏 둘중의 하나만 가져옴
      getLeverage: (symbol: string) => {
        if (get().positions === null) return 1
        return Number(get().positions?.get(`${symbol.toUpperCase()}_LONG`)?.leverage ?? 1)
      },

      // 티커가 오면 선물에서 계산할 것들 계산해서 데이터 정리하는 구조 !!
      getWSTicker: throttle((argsTicker: Map<string, Binance24hrTicker>) => {
        // if (!storeUserInfo.getState().IsLogin()) {
        //   return
        // }

        if (argsTicker.size === 0) {
          return
        }

        const { userHavePosition } = get()
        if (userHavePosition === undefined || !userHavePosition.size) {
          // 포지션 없을 시 unrealizedPNL 초기화
          set((args) => ({ ...args, unrealizedPNL: '0' }))
        }

        let userHaveSymbol: string[] = []

        try {
          userHaveSymbol = Array.from(get().userHavePosition.keys())
        } catch (e) {
          // console.log('error : ', e)
          return
        }

        let unrealizedPNL: number = 0
     
        let CrossUnrealizedPnl = 0
        userHaveSymbol?.forEach((key: string) => {
          const { symbol, positionSide } = DivKey(key)
          const ticker = argsTicker.get(symbol)
          if (ticker) {
            if (positionSide === 'LONG') {
              const [blongside, longPNL, isIsolated] = get().getunRealizedProfit(ticker.s, 'LONG', ticker.c)
              if (blongside === true) {
                unrealizedPNL += longPNL
                // console.log('unrealizedPNL : long ', longPNL)
              }
              if (isIsolated === false) {
                CrossUnrealizedPnl += longPNL
              }
            }

            if (positionSide === 'SHORT') {
              const [bshortside, shortPNL, isIsolated] = get().getunRealizedProfit(ticker.s, 'SHORT', ticker.c)
              if (bshortside === true) {
                unrealizedPNL += shortPNL
              }
              if (isIsolated === false) {
                CrossUnrealizedPnl += shortPNL
              }
            }
          }
        })

        // 현재심볼의 현재가 셋팅하기
        const { exSymbol } = storeFutuExchange.getState()
        const ticker = argsTicker.get(exSymbol)
        if (ticker) {
          storeFutuExchange.getState().setCurrnetPrice(ticker.c)
        }

        set((args) => {
          const baseAvailable = Number(args.userBalance) - Number(args.usedBalance)
          const computedAvailable = Number(baseAvailable) + Number(CrossUnrealizedPnl)
          const clampedAvailable = Math.max(0, computedAvailable)
          return {
            ...args,
            availableBalance: String(clampedAvailable),
            unrealizedPNL: String(unrealizedPNL),
            equity: String(Number(args.userBalance) + Number(CrossUnrealizedPnl)),
          }
        })
      }, 1000),
      // 레버리지를 변경시 사용, ws에서 사용하려고 했는데. 사용불가 - 현재로서는 사용할 일이 없음
      setPosition: (symbol: string, position: FutuAPI_GetPositionList_Res) => {
        // 가지고 있는 것도 레버리지를 올릴 수는 있지. 내릴 수도 있고,
        const LongKey = GetKey(symbol, position.positionSide)
        const havePosition = get().userHavePosition.get(LongKey)
        if (havePosition) {
          // 가지고 있다면. 이렇게 둘다 변경해주고,
          const tmphavePosition = {
            ...havePosition,
            leverage: position.leverage,
            marginType: position.marginType,
          }
          const userHavePositiontmp = get().userHavePosition
          userHavePositiontmp.set(symbol, tmphavePosition)

          set({
            positions: get().positions?.set(symbol, position),
            userHavePosition: userHavePositiontmp,
          })
        } else {
          // 가지고 있지 않는다면 포지션만 변경해준다.
          set({ positions: get().positions?.set(symbol, position) })
        }
      },
      // 갯수가 많치 않기 때문에 array 로 함.
      setOpenOrder: (openOrder: FutuAPI_GetusesrOpenOrders_Res) => {
        const openOrderstmp = new Map(
          Array.from(get().openOrders ?? []).map(([key, el]) => {
            if (el.symbol === openOrder.symbol) {
              return [key, openOrder] // 새로운 값을 대체
            }
            return [key, el]
          }),
        )
        set({ openOrders: openOrderstmp })
      },
      setFutuSymbol: (symbol: string, session?: any) => {
        // 마켓리스트 업데이트
        storeFutuExchange.getState().setExSymbol(symbol)

        // 로그인된 경우에만 포지션 정보 업데이트
        if (session?.NewAuthToken) {
          // 레버리지 찾는거니까 롱만 가쟈옴
          if (get().positions !== null) {
            // 미리 밀어 넣어야겠다.
            const position = get().positions?.get(GetKey(symbol, 'LONG'))
            if (position) {
              set({
                futuSymbol: symbol,
                leverage: Number(position.leverage),
                marginType: setMarginType(position.marginType),
              })
            } else {
              set({ futuSymbol: symbol })
            }
          } else {
            set({ futuSymbol: symbol })
          }
        } else {
          // 로그인되지 않은 경우 심볼만 변경
          set({ futuSymbol: symbol })
        }
      },
      getPosition: (symbol: string, positionSide: string) => {
        return get().positions?.get(GetKey(symbol, positionSide)) || null
      },
      getHavePosition: (symbol: string, positionSide: string) => {
        return get().userHavePosition?.get(GetKey(symbol, positionSide)) || null
      },
      getHavePositionSize: (symbol: string): [string, string] => {
        let longsize = '0'
        let shortsize = '0'
        if (get().getHavePosition === undefined || get().getHavePosition === null) {
          return [longsize, shortsize]
        }

        const longposition = get().getHavePosition(symbol, 'LONG')
        const shortposition = get().getHavePosition(symbol, 'SHORT')
        if (longposition && typeof longposition.positionAmt === 'string') {
          longsize = longposition.positionAmt
        }

        if (shortposition && typeof shortposition.positionAmt === 'string') {
          shortsize = shortposition.positionAmt
        }

        return [longsize, shortsize]
      },

      getunRealizedProfit: (symbol: string, positionSide: string, currentPrice: string) => {
        const havePosition = get().getHavePosition(symbol, positionSide)
        if (havePosition === null) {
          return [false, 0, false]
        }

        const isIsolated = havePosition.marginType === 'isolated'
        const pnl = unPnl(Number(havePosition.entryPrice), Number(currentPrice), Math.abs(Number(havePosition.positionAmt)), positionSide)

        return [true, pnl, isIsolated]
      },
      // openOrder 에서 사용하는 금액 가져오기
      getOpenOrderTotalAmount: (): number | undefined => {
        //
        const openOrder = get().getAllOpenOrders()
        let totalNotional: number = 0

        const position = get().positions
        if (openOrder === null || openOrder === undefined) return 0

        if (isEmptyObject(openOrder)) {
          return 0
        }

        openOrder?.forEach((el) => {
          const obj = position?.get(GetKey(el.symbol, el.positionSide))
          if (obj === undefined) {
            return
          }
          totalNotional += (Number(el.price) * Number(el.origQty)) / Number(obj.leverage ?? 1)
        })

        return totalNotional
      },
      // // 클로즈 사용 가능 발란스
      getAmountToRatioCLOSE: (symbol: string, positionSide: string, ratio: number) => {
        const openOrder = get().getAllOpenOrders()
        const position = get().positions
        let origQty = 0

        const positionAmt = Math.abs(parseFloat(position?.get(GetKey(symbol, positionSide))?.positionAmt ?? '0')) || 0
        const posSide = positionSide === 'SHORT' ? 'BUY' : 'SELL'
        openOrder?.forEach((el) => {
          if (el.symbol === symbol && el.side === posSide && el.positionSide === positionSide) {
            origQty += parseFloat(el.origQty)
          }
        })
        // 기존에 Short Close의 Open Order가 있으면 빼기
        const result = positionAmt - origQty
        const amt = Math.max(result, 0)

        if (ratio >= 100) {
          return amt
        }

        return amt * (ratio / 100.0)
      },
      getOpenOrderTPSLPrice: (symbol: string, positionSide: string): [string, string] => {
        const defualtSend: [string, string] = ['', '']
        const openOrder = get().getAllOpenOrders()
        if (openOrder === null || openOrder === undefined) return defualtSend

        const tmppositionSide = positionSide.toUpperCase()
        if (isEmptyObject(openOrder)) {
          return defualtSend
        }

        if (openOrder.size === 0) {
          return defualtSend
        }

        let tp: string = ''
        let sl: string = ''

        openOrder.forEach((el) => {
          if (el.symbol === symbol && el.positionSide.toUpperCase() === tmppositionSide && el.type === 'STOP_MARKET') {
            sl = el.stopPrice
          }

          if (el.symbol === symbol && el.positionSide.toUpperCase() === tmppositionSide && el.type === 'TAKE_PROFIT_MARKET') {
            tp = el.stopPrice
          }
        })
        return [tp, sl]
      },
      getIsolatedMargin: (symbol: string, positionSide: string): number => {
        if (get().userHavePosition === null || get().userHavePosition === undefined) {
          return -1
        }

        const m_havePosition = get().userHavePosition
        if (IsValid(m_havePosition)) {
          const obj = m_havePosition.get(GetKey(symbol, positionSide))
          if (obj === undefined) return 0

          return Number(obj.isolatedMargin)
        }

        return -1
      },

      setInterval: (interval: string) => {
        set({ interval: interval })
      },

      setFutuLogout: () => {
        // 기존 Map 객체들을 명시적으로 정리
        const currentUserHavePosition = get().userHavePosition;
        const currentOpenOrders = get().openOrders;
        const currentAlgoOpenOrders = get().algoOpenOrders;

        if (currentUserHavePosition) {
          currentUserHavePosition.clear();
        }
        if (currentOpenOrders) {
          currentOpenOrders.clear();
        }
        if (currentAlgoOpenOrders) {
          currentAlgoOpenOrders.clear();
        }

        set({
          userBalance: '',
          unrealizedPNL: '',
          availableBalance: '',
          usedBalance: '',
          leverage: 1,
          balances: null,
          positions: null,
          userHavePosition: new Map<string, FutuAPI_GetPositionList_Res>(),
          openOrders: null,
          algoOpenOrders: null,
        })
      },

      // 알림 관련 함수
      setUnreadCnt: (count: number) => {
        set({ unreadCnt: count })
      },
      incrementNotificationVersion: () => {
        set((state) => ({ notificationVersion: state.notificationVersion + 1 }));
      },
    }),
    {
      name: 'futuSymbol',
      storage: createJSONStorage(() => localStorage),
      // getStorage: () => localStorage,
      partialize: (state) => ({
        futuSymbol: state.futuSymbol,
        interval: state.interval
      }),
    },
  ) as StateCreator<FutuUserInfoData>,
)
